import axios from "axios"
import {
  POST_LOGIN,
  GET_VENDORS_FOR_USER_URL,
  GET_USER_URL,
  GET_ADMIN_BUSINESS_UNITS,
  TEASER_PRODUCTS_URL,
  CATEGORY_FOR_FORM_URL,
  ORDER_CHECKOUT_URL,
  ORDER_URL,
  PRODUCT_USER_TAGS_URL,
  POST_USER_RESET,
  VALIDATE_2FA_URL,
  RESEND_2FA_URL,
  GET_USER_PROFILE_URL,
  ADMIN_USER_URL,
} from "helpers/url_helper"
import { LOCAL_STORAGE_TAGS, URL_PATHS } from "constants/index"
import { app } from "config"

const APP_VERSION = app.currentVersion //update in config.js file, before building

//apply base url for axios
const APP_API_URL = `${app.APP_LAYER}/v${APP_VERSION}`
const VENDORS_API_URL = `${app.VENDORS_LAYER}/v${APP_VERSION}`
const IMAGE_API_URL = `${app.IMAGE_LAYER}/v${APP_VERSION}`
const VIDEO_API_URL = `${app.VIDEO_LAYER}/v${APP_VERSION}`
const POINTS_API_URL = `${app.POINTS_LAYER}/v${APP_VERSION}`
const POLLS_API_URL = `${app.POLLS_LAYER}/v${APP_VERSION}`

// app layer related
const appAxiosApi = axios.create({
  baseURL: APP_API_URL,
  headers: { "X-Requested-From": "cms2" },
  params: { nc: new Date().getTime() },
})
appAxiosApi.interceptors.request.use(request => {
  request.params = {
    ...request.params,
    nc: new Date().getTime(),
  }

  if (
    (request.method === "get" ||
      request.method === "delete" ||
      request.method === "put") &&
    !request.url.includes(GET_VENDORS_FOR_USER_URL) &&
    !request.url.includes(TEASER_PRODUCTS_URL) &&
    !request.url.includes(CATEGORY_FOR_FORM_URL) &&
    !request.url.includes(PRODUCT_USER_TAGS_URL) &&
    !request.url.includes(GET_USER_PROFILE_URL) &&
    !request.url.includes(POST_USER_RESET)
  ) {
    request.headers = {
      ...request.headers,
      ["Authorization"]: `Bearer ${
        JSON.parse(localStorage.getItem(LOCAL_STORAGE_TAGS.AUTH_USER)).api_token
      }`,
    }
    request.params = {
      vendor_id: JSON.parse(
        localStorage.getItem(LOCAL_STORAGE_TAGS.VENDOR_ID_TAG)
      ),
      ...request.params,
    }
  }
  if (request.method === "post" && request.data instanceof FormData) {
    request.headers = {
      "Content-Type": "multipart/form-data",
      "X-Requested-From": "cms2",
    }
  }
  if (
    request.url.includes(GET_ADMIN_BUSINESS_UNITS) &&
    request.method === "get"
  ) {
    request.headers = {
      ...request.headers,
      ["Authorization"]: `Bearer ${
        JSON.parse(localStorage.getItem(LOCAL_STORAGE_TAGS.AUTH_USER)).api_token
      }`,
    }
    request.params = {
      ...request.params,
    }
  }
  if (
    !request.url.includes(POST_LOGIN) &&
    !request.url.includes(VALIDATE_2FA_URL) &&
    !request.url.includes(RESEND_2FA_URL) &&
    !request.url.includes(ORDER_CHECKOUT_URL) &&
    !request.url.includes(POST_USER_RESET) &&
    request.method === "post"
  ) {
    request.headers = {
      ...request.headers,
      ["Authorization"]: `Bearer ${
        JSON.parse(localStorage.getItem(LOCAL_STORAGE_TAGS.AUTH_USER)).api_token
      }`,
    }
    request.params = {
      ...request.params,
      vendor_id: JSON.parse(
        localStorage.getItem(LOCAL_STORAGE_TAGS.VENDOR_ID_TAG)
      ),
    }
  }
  return request
})
appAxiosApi.interceptors.response.use(
  response => {
    return response
  },
  error => {
    if (error?.response?.status === 403 || error?.response?.status === 401) {
      localStorage.removeItem(LOCAL_STORAGE_TAGS.AUTH_USER)
      localStorage.removeItem(LOCAL_STORAGE_TAGS.VENDOR_ID_TAG)
      localStorage.removeItem(LOCAL_STORAGE_TAGS.CLIENTS_TAG)
      localStorage.removeItem(LOCAL_STORAGE_TAGS.FEATURES_TAG)
      localStorage.removeItem(LOCAL_STORAGE_TAGS.CONFIG_TAG)
      localStorage.removeItem(LOCAL_STORAGE_TAGS.USER_PERMISSIONS_TAG)
      window.location.href = URL_PATHS.LOGIN
    }
    return Promise.reject(error)
  }
)

export async function get(url, config = {}) {
  return await appAxiosApi
    .get(url, { ...config })
    .then(response => response.data)
}

export async function post(url, data, config = {}) {
  return appAxiosApi
    .post(url, data, { ...config })
    .then(response => response.data)
}

export async function axiosPut(url, data, config = {}) {
  return appAxiosApi
    .put(url, { ...data }, { ...config })
    .then(response => response.data)
}

export async function del(url, config = {}) {
  return await appAxiosApi
    .delete(url, { params: { ...config } })
    .then(response => response.data)
}

//vendors layer related
const vendorsAxiosApi = axios.create({
  baseURL: VENDORS_API_URL,
  headers: { "X-Requested-From": "cms2" },
  params: { nc: new Date().getTime() },
})
vendorsAxiosApi.interceptors.request.use(request => {
  request.params = {
    ...request.params,
    nc: new Date().getTime(),
  }
  request.headers = {
    ...request.headers,
    ["Authorization"]: `Bearer ${
      JSON.parse(localStorage.getItem(LOCAL_STORAGE_TAGS.AUTH_USER)).api_token
    }`,
  }
  request.params = {
    vendor_id: JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_TAGS.VENDOR_ID_TAG)
    ),
    ...request.params,
  }
  return request
})
vendorsAxiosApi.interceptors.response.use(
  response => response,
  error => Promise.reject(error)
)

export async function getVendorsAxios(url, config = {}) {
  return await vendorsAxiosApi
    .get(url, { ...config })
    .then(response => response.data)
}

export async function postVendorsAxios(url, data, config = {}) {
  return vendorsAxiosApi
    .post(url, data, { ...config })
    .then(response => response.data)
}

export async function putVendorsAxios(url, data, config = {}) {
  return vendorsAxiosApi
    .put(url, { ...data }, { ...config })
    .then(response => response.data)
}

export async function delVendorsAxios(url, data, config = {}) {
  return await vendorsAxiosApi
    .delete(url, { ...config })
    .then(response => response.data)
}

export async function patchVendorsAxios(url, data, config = {}) {
  return vendorsAxiosApi
    .patch(url, { ...data }, { ...config })
    .then(response => response.data)
}

//points layer related
const pointsAxiosApi = axios.create({
  baseURL: POINTS_API_URL,
  headers: {
    "X-Requested-From": "cms2",
  },
  params: { nc: new Date().getTime() },
})

pointsAxiosApi.interceptors.request.use(request => {
  request.headers = {
    ...request.headers,
    ["Authorization"]: `Bearer ${
      JSON.parse(localStorage.getItem(LOCAL_STORAGE_TAGS.AUTH_USER)).api_token
    }`,
  }
  request.params = {
    ...request.params,
    nc: new Date().getTime(),
  }
  return request
})

pointsAxiosApi.interceptors.response.use(
  response => response,
  error => Promise.reject(error)
)

export async function getPointsAxios(url, config = {}) {
  return await pointsAxiosApi
    .get(url, { ...config })
    .then(response => response.data)
}

export async function getPointsAxiosWithParams(url, config = {}) {
  if (!config.params) {
    config.params = {}
  }
  config.params = {
    // api_token: JSON.parse(localStorage.getItem(LOCAL_STORAGE_TAGS.AUTH_USER))
    //   .api_token,
    vendor_id: JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_TAGS.VENDOR_ID_TAG)
    ),
    ...config.params,
  }
  return await pointsAxiosApi
    .get(url, { ...config })
    .then(response => response.data)
}

export async function postPointsAxios(url, data, config = {}) {
  return pointsAxiosApi
    .post(url, data, { ...config })
    .then(response => response.data)
}

export async function putPointsAxios(url, data, config = {}) {
  return pointsAxiosApi
    .put(url, { ...data }, { ...config })
    .then(response => response.data)
}

export async function delPointsAxios(url, data, config = {}) {
  return await pointsAxiosApi
    .delete(url, { ...config })
    .then(response => response.data)
}

export async function delPointsAxiosWithParams(url, config = {}) {
  return await pointsAxiosApi
    .delete(url, {
      params: {
        ...config,
        vendor_id: JSON.parse(
          localStorage.getItem(LOCAL_STORAGE_TAGS.VENDOR_ID_TAG)
        ),
      },
    })
    .then(response => response.data)
}

//polls layer related
const pollsAxiosApi = axios.create({
  baseURL: POLLS_API_URL,
  headers: {
    "X-Requested-From": "cms2",
  },
  params: { nc: new Date().getTime() },
})

pollsAxiosApi.interceptors.request.use(request => {
  request.headers = {
    ...request.headers,
    ["Authorization"]: `Bearer ${
      JSON.parse(localStorage.getItem(LOCAL_STORAGE_TAGS.AUTH_USER)).api_token
    }`,
  }
  request.params = {
    ...request.params,
    nc: new Date().getTime(),
  }
  return request
})

pollsAxiosApi.interceptors.response.use(
  response => response,
  error => Promise.reject(error)
)

export async function getPollsAxios(url, config = {}) {
  return await pollsAxiosApi
    .get(url, { ...config })
    .then(response => response.data)
}

export async function getPollsAxiosWithParams(url, config = {}) {
  if (!config.params) {
    config.params = {}
  }
  config.params = {
    vendor_id: JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_TAGS.VENDOR_ID_TAG)
    ),
    ...config.params,
  }
  return await pollsAxiosApi
    .get(url, { ...config })
    .then(response => response.data)
}

export async function postPollsAxios(url, data, config = {}) {
  return pollsAxiosApi
    .post(url, data, { ...config })
    .then(response => response.data)
}

export async function putPollsAxios(url, data, config = {}) {
  return pollsAxiosApi
    .put(url, { ...data }, { ...config })
    .then(response => response.data)
}

export async function delPollsAxios(url, config = {}) {
  return await pollsAxiosApi
    .delete(url, { params: { ...config } })
    .then(response => response.data)
}

//image layer related
const imageAxiosApi = axios.create({
  baseURL: IMAGE_API_URL,
  headers: {
    "X-Requested-From": "cms2",
  },
  params: { nc: new Date().getTime() },
})

imageAxiosApi.interceptors.request.use(request => {
  request.headers = {
    ...request.headers,
    ["Authorization"]: `Bearer ${
      JSON.parse(localStorage.getItem(LOCAL_STORAGE_TAGS.AUTH_USER)).api_token
    }`,
  }
  request.params = {
    ...request.params,
    nc: new Date().getTime(),
  }
  return request
})

imageAxiosApi.interceptors.response.use(
  response => response,
  error => Promise.reject(error)
)

export async function getImageAxios(url, config = {}) {
  return await imageAxiosApi.get(url, { ...config })
}

export async function postImageAxios(url, data, config = {}) {
  return imageAxiosApi
    .post(url, data, { ...config })
    .then(response => response.data)
}

//video layer related
const videoAxiosApi = axios.create({
  baseURL: VIDEO_API_URL,
  headers: {
    "X-Requested-From": "cms2",
  },
  params: { nc: new Date().getTime() },
})

videoAxiosApi.interceptors.request.use(request => {
  request.headers = {
    ...request.headers,
    ["Authorization"]: `Bearer ${
      JSON.parse(localStorage.getItem(LOCAL_STORAGE_TAGS.AUTH_USER)).api_token
    }`,
  }
  request.params = {
    ...request.params,
    nc: new Date().getTime(),
  }
  return request
})

videoAxiosApi.interceptors.response.use(
  response => response,
  error => Promise.reject(error)
)

export async function getVideoAxios(url, config = {}) {
  return await videoAxiosApi
    .get(url, { ...config })
    .then(response => response.data)
}

export async function postVideoAxios(url, data, config = {}) {
  return videoAxiosApi
    .post(url, data, { ...config })
    .then(response => response.data)
}
